import * as React from "react";
import { CalendarIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { format } from "date-fns";

interface DatePickerProps {
    value?: Date;
    callback?: (value: Date | undefined) => void;
    className?: string;
    style?: React.CSSProperties;
    disablePastDates?: boolean;
    disabled?: boolean;
    /** When false, avoids focus-trap behavior that can break inputs inside complex layouts (e.g. signing HTML). */
    modal?: boolean;
}

export function DatePicker({
    value,
    callback,
    className,
    style,
    disablePastDates = false,
    disabled = false,
    modal = false,
}: DatePickerProps) {
    const [date, setDate] = React.useState<Date | undefined>(value);
    const [open, setOpen] = React.useState(false);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const handleSelect = (selectedDate: Date | undefined) => {
        if (!selectedDate) return;
        setDate(selectedDate);
        callback?.(selectedDate);
        setOpen(false);
    };

    React.useEffect(() => {
        setDate(value);
    }, [value]);

    return (
        <Popover open={open} onOpenChange={setOpen} modal={modal}>
            <PopoverTrigger asChild>
                <Button
                variant="outline"
                style={style}
                disabled={disabled}
                className={`justify-start text-left ${
                    !date ? "text-muted-foreground" : ""
                } ${className || ""}`}>
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {date ? format(date, "PPP") : "Pick a date"}
                </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0">
                <Calendar
                mode="single"
                selected={date}
                defaultMonth={date}
                onSelect={handleSelect}
                captionLayout="dropdown"
                disabled={disablePastDates ? (day) => {
                    const d = new Date(day);
                    d.setHours(0,0,0,0);
                    return d < today; 
                } : undefined}/>
            </PopoverContent>
        </Popover>
    );
}
